import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { setToastSettings } from '@/lib/toast';
import { ToastSettings } from '@/types/toast';

const TOAST_SETTINGS_CACHE_KEY = 'clando-toast-settings-cache-v1';
const TOAST_SETTINGS_REFETCH_MIN_INTERVAL_MS = 30 * 60 * 1000;

function loadToastSettingsFromCache(): ToastSettings {
    try {
        const raw = localStorage.getItem(TOAST_SETTINGS_CACHE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

function saveToastSettingsToCache(settings: ToastSettings) {
    try { localStorage.setItem(TOAST_SETTINGS_CACHE_KEY, JSON.stringify(settings)); } catch {}
}

export function useToastSettings() {
    const [settings, setSettings] = useState<ToastSettings>(() => {
        const cached = loadToastSettingsFromCache();
        // Apply cached settings immediately on init
        if (Object.keys(cached).length > 0) setToastSettings(cached);
        return cached;
    });
    const [loading, setLoading] = useState(false);

    const lastFetchRef = useRef<number>(0);

    const fetchSettings = async () => {
        try {
            const { data, error } = await supabase
                .from('app_configuration' as any)
                .select('value')
                .eq('key', 'toast_settings')
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    setToastSettings({});
                    setSettings({});
                } else {
                    throw error;
                }
            } else {
                const configData = data as any;
                const value = configData.value as ToastSettings;
                setToastSettings(value || {});
                setSettings(value || {});
                saveToastSettingsToCache(value || {});
            }
        } catch (err) {
            console.error('Failed to fetch toast settings:', err);
        } finally {
            setLoading(false);
            lastFetchRef.current = Date.now();
        }
    };

    useEffect(() => {
        fetchSettings();

        if (typeof document === 'undefined') return;
        // Realtime removed — admin-only setting that almost never changes.
        // Throttled refetch when the tab regains focus (30 min min interval).
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastFetchRef.current < TOAST_SETTINGS_REFETCH_MIN_INTERVAL_MS) return;
            fetchSettings();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    return { settings, loading, refresh: fetchSettings };
}
